import {
    createAsyncThunk,
    createSlice,
    PayloadAction,
    current,
} from "@reduxjs/toolkit";
import { RootState, AppThunk } from "../../app/store";
import { fetchTicket } from "./ticketAPI";
import { Metadata, Content, TimedObject } from "../../util/types";
import { Filter } from "../filter/filterSlice";

/*
 * TicketItem is just like Ticket but without content
 **/
export interface TicketItem {
    metadata: Metadata;
    time: TimedObject;
    parent_id: string;
    context_id: string;
    area_id: string;
    space_id: string;
    ktype: string | null;
    star: boolean;
    title: string;
    tag: string;
}

export interface Ticket {
    metadata: Metadata;
    time: TimedObject;
    parent_id: string;
    context_id: string;
    area_id: string;
    space_id: string;
    ktype: string | null;
    star: boolean;
    title: string;
    tag: string;
    content: Content;
}

export interface TicketEditRequest {
    id: string | null;
    old_id: string | null;
    user_id: string | null;
    admin_id: string | null;
    organization_id: string | null;
    team_id: string | null;
    kprivate: boolean;

    start: string | null;
    end: string | null;
    planned_start: string | null;
    planned_end: string | null;
    deadline: string | null;

    parent_id: string | null;
    context_id: string | null;
    area_id: string | null;
    space_id: string | null;
    ktype: string | null;
    star: boolean;
    title: string;
    tag: string;
    content: string;
    content_type: string;
}

export const toTicketItem = (t: Ticket): TicketItem => {
    return {
        metadata: { ...t.metadata },
        time: { ...t.time },
        parent_id: t.parent_id,
        context_id: t.context_id,
        area_id: t.area_id,
        space_id: t.space_id,
        ktype: t.ktype,
        star: t.star,
        title: t.title,
        tag: t.tag,
    };
};

export const toEditRequest = (t: Ticket | null): TicketEditRequest => {
    if (t) {
        return {
            id: t.metadata.id,
            old_id: t.metadata.old_id,
            user_id: t.metadata.user.id,
            admin_id: null, //t.metadata.admin.id,
            organization_id: null, //t.metadata.organization.id,
            team_id: null, //t.metadata.team_id,
            kprivate: t.metadata.kprivate,

            start: t.time.start,
            end: t.time.end,
            planned_start: t.time.planned_start,
            planned_end: t.time.planned_end,
            deadline: t.time.deadline,

            parent_id: t.parent_id,
            context_id: t.context_id,
            area_id: t.area_id,
            space_id: t.space_id,
            ktype: t.ktype,
            star: t.star,
            title: t.title,
            tag: t.tag,
            content: t.content.content,
            content_type: "Markdown",
        };
    } else {
        return {
            id: null,
            old_id: null,
            user_id: null,
            admin_id: null,
            organization_id: null,
            team_id: null,
            kprivate: true,

            start: null,
            end: null,
            planned_start: null,
            planned_end: null,
            deadline: null,

            parent_id: null,
            context_id: null,
            area_id: null,
            space_id: null,
            ktype: "6",
            star: false,
            title: "title",
            tag: "priv",
            content: "content",
            content_type: "Markdown",
        };
    }
};
// */
export function displayKType(i: string | null) {
    switch (i) {
        case "1":
            return "Area";
        case "2":
            return "Goal";
        case "3":
            return "Project";
        case "4":
            return "Epic";
        case "5":
            return "Del";
        case "6":
            return "Task";
        default:
            return "Unknown";
    }
}
export interface TicketItemPeriod {
    items: { [key: string]: TicketItem };
    min: string;
    max: string;
}

export interface TicketState {
    store: { [key: string]: Ticket };
    items: { [key: string]: TicketItem };
    unidentified: { [key: string]: Ticket };
}

export interface ShiftPlannedStartsRequest {}

const initialState: TicketState = {
    store: {},
    items: {},
    unidentified: {},
};

function filterItems(
    store: { [key: string]: TicketItem },
    min: string,
    max: string,
    predicate: (item: TicketItem) => boolean,
): { [key: string]: TicketItem } {
    return Object.fromEntries(
        Object.entries(store).filter(([key, value]) => predicate(value)),
    );
}

function rmByPeriod(
    store: { [key: string]: TicketItem },
    min: string,
    max: string,
): { [key: string]: TicketItem } {
    return filterItems(store, min, max, (value) => {
        if (
            value.time.planned_start &&
            value.time.planned_start.localeCompare(min) > 0 &&
            value.time.planned_start.localeCompare(max) < 0 &&
            value.ktype == "6"
        )
            return false;
        return true;
    });
}

export function childLevels(ktype: string) {
    const levels = {
        level_1: false,
        level_2: false,
        level_3: false,
        level_4: false,
        level_5: false,
        level_6: false,
    };

    const index = Number(ktype) - 1;

    if (index >= 1 && index <= 6) {
        levels[`level_${index}` as keyof typeof levels] = true;
        if (index === 5) {
            levels[`level_3`] = true;
        }
    } else {
        levels.level_1 = true;
    }

    return levels;
}

export const ticketSlice = createSlice({
    name: "ticket",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        upsertTicket: (state, action: PayloadAction<Ticket>) => {
            const id = action.payload.metadata.id;
            if (id) {
                state.store[id] = action.payload;
                return state;
            } else {
                return state;
            }
        },
        upsertTickets: (
            state,
            action: PayloadAction<{ [key: string]: Ticket }>,
        ) => {
            state.store = { ...state.store, ...action.payload };
        },
        upsertTicketItems: (
            state,
            action: PayloadAction<{ [key: string]: TicketItem }>,
        ) => {
            //c onsole.log("merging items and payload", current(state), action.payload);
            state.items = { ...state.items, ...action.payload };
            return state;
        },
        upsertTicketItemsPeriod: (
            state,
            action: PayloadAction<TicketItemPeriod>,
        ) => {
            //c onsole.log("merging items and payload", current(state), action.payload);
            const s = rmByPeriod(
                state.items,
                action.payload.min,
                action.payload.max,
            );
            //state.items = {...state.items, ...action.payload.items};
            state.items = { ...s, ...action.payload.items };
            return state;
        },
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    /*
  extraReducers: (builder) => {
    builder
      .addCase(incrementAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(incrementAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.value += action.payload;
      })
      .addCase(incrementAsync.rejected, (state) => {
        state.status = 'failed';
      });
  },
  // */
});

export const {
    upsertTicket,
    upsertTickets,
    upsertTicketItems,
    upsertTicketItemsPeriod,
} = ticketSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectTicket = (id: string) => (state: RootState) => {
    if (state.ticket.store[id]) return state.ticket.store[id];
    return null;
};

export const selectByIds = (ids: string[]) => (state: RootState) => {
    let tis: { [key: string]: TicketItem } = {};
    for (const key of ids) {
        if (state.ticket.items[key]) tis[key] = state.ticket.items[key];
    }
    return tis;
};

export const selectChildrenItems =
    (id: string | null) => (state: RootState) => {
        /*
	let children: {[key:string]: Ticket} = filter(
		state.ticket.items, (v, k) => v.parent_id === id
		);
	   // */
        let children: { [key: string]: TicketItem } = {};
        for (const key in state.ticket.items) {
            const ti = state.ticket.items[key];
            if (ti.parent_id === id) {
                children[key] = ti;
            }
        }
        var childrenR = Object.keys(children).map(function (idx) {
            let child = children[idx];
            // do something with person
            return child;
        });
        return childrenR;
    };

export const selectPeriod =
    (start: string, end: string) => (state: RootState) => {
        let plannedStartInPeriod: TicketItem[] = [];
        for (const key in state.ticket.items) {
            const ti = state.ticket.items[key];
            if (
                ti.time.planned_start &&
                ti.time.planned_start.localeCompare(start) > 0 &&
                ti.time.planned_start.localeCompare(end) < 0 &&
                ti.ktype == "6"
            ) {
                plannedStartInPeriod.push(ti);
            }
        }
        return plannedStartInPeriod;
    };

export const selectContext =
    (context_id: string | null, end: string) => (state: RootState) => {
        let plannedStartInPeriod: TicketItem[] = [];
        for (const key in state.ticket.items) {
            const ti = state.ticket.items[key];
            if (
                ti.context_id &&
                context_id &&
                ti.context_id === context_id &&
                ti.ktype == "6"
            ) {
                if (!ti.time.end) plannedStartInPeriod.push(ti);
                if (ti.time.end && ti.time.end > end)
                    plannedStartInPeriod.push(ti);
            }
        }
        return plannedStartInPeriod;
    };

export const selectByFilter = (state: RootState, f: Filter) => {
    let filtered: TicketItem[] = [];
    for (const key in state.ticket.items) {
        const ti = state.ticket.items[key];
        if (matchFilter(f, ti)) {
            filtered.push(ti);
        }
    }
    return filtered;
};

/*
// Preorder traversal function to return a list of Items starting from a given id
// The children are sorted by the 'start' property
function preorderTraversal(store: Store, id: string): Item[] {
  const result: Item[] = [];

  // Helper function to perform the traversal
  function traverse(item: Item): void {
    result.push(item); // Add the current item to the result list

    // Find the children of the current item
    const children = Object.values(store).filter(currentItem => currentItem.parent_id === item.id);

    // Sort the children by the 'start' property (ISO date string)
    children.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    // Recursively visit each child in the sorted order
    for (const child of children) {
      traverse(child);
    }
  }

  // Find the starting item by id
  const startingItem = store[id];
  if (startingItem) {
    traverse(startingItem);
  }

  return result;
}
// */

const matchFilter = (f: Filter, ti: TicketItem) => {
    if (
        (f.planned_start_min && ti.time.planned_start === null) ||
        (f.planned_start_min &&
            ti.time.planned_start &&
            f.planned_start_min >= ti.time.planned_start)
    ) {
        return false;
    }
    if (
        (f.planned_start_max && ti.time.planned_start === null) ||
        (f.planned_start_max &&
            ti.time.planned_start &&
            f.planned_start_max <= ti.time.planned_start)
    ) {
        return false;
    }
    return true;
};

export const selectTickets = (state: RootState) => state.ticket.store;

export default ticketSlice.reducer;
